import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/v2/ui/Dialog'
import { Button } from '@/components/v2/ui/Button'
import { Input } from '@/components/v2/ui/Input'
import { SegmentConditionList } from './SegmentConditionList'
import { SegmentLiveCount } from './SegmentLiveCount'
import { useSegmentRuleState } from './useSegmentRuleState'
import type { ProductSegmentRule } from './segmentPresets'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

interface Props {
  open: boolean
  onClose: () => void
  onSave: (name: string, rule: ProductSegmentRule) => void
  /** Must be stable (memoize via useCallback in the caller), else unnecessary timer resets. */
  countFn: (filters: ProductTableFilters) => Promise<number>
  initialRule?: ProductSegmentRule
  initialName?: string
}

export function SegmentRuleBuilder({
  open, onClose, onSave, countFn, initialRule, initialName,
}: Props) {
  const { t } = useTranslation()
  const { rule, keys, addCondition, updateCondition, removeCondition } =
    useSegmentRuleState(initialRule)
  const [name, setName] = useState(initialName ?? '')

  function handleSave() {
    if (!name.trim()) return
    onSave(name.trim(), rule)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('productSegments.builder.title')}</DialogTitle>
        </DialogHeader>

        {/* Match toggle — ODER disabled (S2 only supports 'all') */}
        <div className="flex items-center gap-2 text-sm mt-2">
          <span className="font-medium">{t('productSegments.builder.matchAll')}</span>
          <span className="text-neutral-400 dark:text-neutral-500">
            {t('productSegments.builder.matchAny')} ({t('productSegments.soon')})
          </span>
        </div>

        <SegmentConditionList
          rule={rule}
          itemKeys={keys}
          onAdd={addCondition}
          onChange={updateCondition}
          onRemove={removeCondition}
        />

        <SegmentLiveCount rule={rule} countFn={countFn} />

        <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            data-testid="builder-name"
            placeholder={t('productSegments.builder.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t('productSegments.builder.name')}
          />
          <Button
            data-testid="builder-save"
            variant="primary"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            {t('productSegments.builder.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

SegmentRuleBuilder.displayName = 'SegmentRuleBuilder'
